<template>
  <footer class="footer">
    <div class="footer-main">
      <div class="footer-container">
        <div class="footer-grid">
          <div class="footer-column footer-brand">
            <div class="footer-logo">
              <img v-if="company?.logo" :src="company.logo" class="footer-logo-img" alt="MegaMaker">
              <template v-else>
                <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                  <rect x="5" y="10" width="30" height="20" stroke="white" stroke-width="2.5" fill="none"/>
                  <line x1="20" y1="10" x2="20" y2="5" stroke="#2196F3" stroke-width="2.5"/>
                  <circle cx="20" cy="25" r="4" fill="#2196F3"/>
                  <rect x="10" y="30" width="20" height="2.5" fill="white"/>
                </svg>
                <span>MegaMaker</span>
              </template>
            </div>
            <p class="footer-desc">{{ footerDesc }}</p>
            <div v-if="company && company.mpQr" class="footer-qr">
              <img :src="company.mpQr" alt="公众号二维码" class="footer-qr-img" />
              <span class="footer-qr-text">{{ $t('about.follow') }}</span>
            </div>
          </div>

          <div class="footer-column">
            <h3 class="footer-title">{{ $t('footer.products') }}</h3>
            <ul class="footer-links">
              <li><a href="/equipment/single-wire">{{ $t('nav.equipmentSingle') }}</a></li>
              <li><a href="/equipment/multi-beam">{{ $t('nav.equipmentMulti') }}</a></li>
              <li><a href="/equipment/titanium">{{ $t('nav.equipmentTitanium') }}</a></li>
              <li><a href="/services/lattice">{{ $t('nav.lattice') }}</a></li>
              <li><a href="/services/concrete">{{ $t('nav.concrete') }}</a></li>
            </ul>
          </div>

          <div class="footer-column">
            <h3 class="footer-title">{{ $t('footer.solutions') }}</h3>
            <ul class="footer-links">
              <li><a href="/solutions/ship">{{ $t('nav.ship') }}</a></li>
              <li><a href="/solutions/platform">{{ $t('nav.platform') }}</a></li>
              <li><a href="/solutions/deep-sea">{{ $t('nav.deepSea') }}</a></li>
            </ul>
          </div>

          <div class="footer-column">
            <h3 class="footer-title">{{ $t('footer.about') }}</h3>
            <ul class="footer-links">
              <li><a href="/about">{{ $t('footer.intro') }}</a></li>
              <li><a href="/news">{{ $t('footer.news') }}</a></li>
              <li><a href="#" @click.prevent="openQuote">{{ $t('footer.contact') }}</a></li>
            </ul>
          </div>

          <div class="footer-column">
            <h3 class="footer-title">{{ $t('footer.contactTitle') }}</h3>
            <ul class="footer-contact">
              <li>{{ contact.address }}</li>
              <li>{{ contact.phone }}</li>
              <li>{{ contact.email }}</li>
              <li>{{ contact.hours }}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <div class="footer-bottom">
      <div class="footer-container">
        <div class="footer-bottom-content">
          <div class="footer-copyright-block">
            <p class="copyright">{{ $t('footer.copyright') }}</p>
            <a
              class="icp-link"
              href="https://beian.miit.gov.cn/"
              target="_blank"
              rel="noopener noreferrer"
            >京ICP备2026066746号</a>
          </div>
          <div class="footer-bottom-links">
            <a href="#" @click.prevent="openPolicy('privacy')">{{ $t('footer.privacy') }}</a>
            <span class="divider">|</span>
            <a href="#" @click.prevent="openPolicy('terms')">{{ $t('footer.terms') }}</a>
            <span class="divider">|</span>
            <a href="#" @click.prevent="openSiteMap">{{ $t('footer.sitemap') }}</a>
          </div>
        </div>
      </div>
    </div>

    <!-- 隐私政策 / 使用条款弹窗 -->
    <transition name="policy-fade">
      <div v-if="policyDialog" class="policy-mask" @click.self="closePolicy">
        <div class="policy-panel" role="dialog" aria-modal="true">
          <div class="policy-head">
            <h4>{{ policyTitle }}</h4>
            <button class="policy-close" @click="closePolicy" aria-label="close">×</button>
          </div>
          <div class="policy-body">
            <p v-for="(para, i) in policyParagraphs" :key="i" class="policy-para">{{ para }}</p>
          </div>
          <div class="policy-foot">
            <button class="policy-ok" @click="closePolicy">{{ $t('footer.gotIt') }}</button>
          </div>
        </div>
      </div>
    </transition>
  </footer>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { legalPolicyApi, companyApi, systemConfigApi } from '../api'
import { openInquiry } from '../utils/inquiry'

export default {
  name: 'Footer',
  setup() {
    const route = useRoute()
    const { tm, t, locale } = useI18n()
    const company = ref(null)

    // 系统配置(后台 系统配置页维护)：页尾网站地图链接 + 联系方式
    const configMap = ref({})
    const loadSiteMap = async () => {
      try {
        configMap.value = (await systemConfigApi()) || {}
      } catch (e) {
        configMap.value = {}
      }
    }
    const mapUrl = computed(() => configMap.value.map_url || '')

    // 页尾联系方式：读系统配置(配置键 footer_address / footer_phone / footer_email / footer_hours)，
    // 未配置回退内置文案；英文版优先取 *_en，其次取中文键
    const contact = computed(() => {
      const en = locale.value === 'en-US'
      const c = configMap.value
      const pick = (base, i18nKey) => (en
        ? (c[base + '_en'] || c[base] || t(i18nKey))
        : (c[base] || t(i18nKey)))
      return {
        address: pick('footer_address', 'footer.address'),
        phone: pick('footer_phone', 'footer.phone'),
        email: pick('footer_email', 'footer.email'),
        hours: pick('footer_hours', 'footer.hours')
      }
    })

    // 页尾标语：读系统配置(配置键 footer_desc / footer_desc_en)，未配置回退内置 i18n
    const footerDesc = computed(() => {
      const en = locale.value === 'en-US'
      const c = configMap.value
      return (en ? (c.footer_desc_en || c.footer_desc) : c.footer_desc) || t('footer.desc')
    })

    // 每次进入页面 / 切换路由都拉取最新公司信息（二维码后台上传后即时生效）
    const loadCompany = async () => {
      try {
        company.value = await companyApi()
      } catch (e) {
        // 失败静默
      }
    }



    onMounted(() => {
      loadCompany()
      loadSiteMap()
    })
    watch(() => route.path, () => {
      loadCompany()
      loadSiteMap()
    })

    // 联系我们：打开全局报价/咨询弹窗
    const openQuote = () => openInquiry()

    // 网站地图：新标签页打开后台配置的百度地图链接(未配置时给出提示)
    const openSiteMap = () => {
      const url = mapUrl.value
      if (url) {
        window.open(url, '_blank', 'noopener')
      } else {
        window.alert(t('footer.mapPending'))
      }
    }

    // ===== 隐私政策 / 使用条款弹窗 =====
    const policyDialog = ref(null) // 'privacy' | 'terms' | null
    const remotePolicy = ref(null) // 后台配置的政策内容（legal_policy 表）

    const openPolicy = async (type) => {
      policyDialog.value = type
      remotePolicy.value = null
      try {
        const data = await legalPolicyApi(type)
        remotePolicy.value = data || null
      } catch (e) {
        // 拉取失败则回退内置文案
        remotePolicy.value = null
      }
    }
    const closePolicy = () => {
      policyDialog.value = null
    }
    const onKey = (e) => {
      if (e.key === 'Escape') closePolicy()
    }
    onMounted(() => document.addEventListener('keydown', onKey))
    onUnmounted(() => document.removeEventListener('keydown', onKey))
    watch(policyDialog, (v) => {
      document.body.style.overflow = v ? 'hidden' : ''
    })

    // 标题：后台配置优先，其次内置 i18n
    const policyTitle = computed(() => {
      const pt = policyDialog.value
      if (!pt) return ''
      const remote = remotePolicy.value
      if (remote) {
        const val = locale.value === 'en-US' ? (remote.titleEn || remote.title) : remote.title
        if (val) return val
      }
      return pt === 'privacy' ? t('footer.privacy') : t('footer.terms')
    })

    // 段落：后台配置正文（按空行分段）优先，其次内置 i18n
    const policyParagraphs = computed(() => {
      const t = policyDialog.value
      if (!t) return []
      const remote = remotePolicy.value
      if (remote) {
        const raw = locale.value === 'en-US' ? (remote.contentEn || remote.content) : remote.content
        if (raw) {
          return raw.split(/\n{2,}/).map((s) => s.trim()).filter(Boolean)
        }
      }
      const list = tm(`legal.${t}`)
      return Array.isArray(list) ? list : []
    })

    return { company, contact, footerDesc, openQuote, openSiteMap, policyDialog, policyTitle, policyParagraphs, openPolicy, closePolicy }
  }
}
</script>

<style scoped>
.footer {
  background: #0B1A2D;
  color: #FFFFFF;
}

.footer-main {
  padding: 80px 0 40px;
}

.footer-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 40px;
}

.footer-grid {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1.5fr;
  gap: 60px;
}

.footer-brand {
  padding-right: 40px;
}

.footer-logo-img {
  height: 40px;
  width: auto;
  max-width: 200px;
  object-fit: contain;
}

.footer-logo {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  font-size: 24px;
  font-weight: 700;
}

.footer-desc {
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  line-height: 1.8;
  margin-bottom: 24px;
}

.footer-qr {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 24px;
}

.footer-qr-img {
  width: 108px;
  height: 108px;
  object-fit: contain;
  background: #FFFFFF;
  border-radius: 8px;
  padding: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
}

.footer-qr-text {
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
  line-height: 1.6;
  max-width: 120px;
}

.footer-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 24px;
  color: #FFFFFF;
}

.footer-links {
  list-style: none;
  padding: 0;
  margin: 0;
}

.footer-links li {
  margin-bottom: 12px;
}

.footer-links a {
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  font-size: 14px;
  transition: all 0.3s ease;
  display: inline-block;
}

.footer-links a:hover {
  color: #2196F3;
  transform: translateX(4px);
}

.footer-contact {
  list-style: none;
  padding: 0;
  margin: 0;
}

.footer-contact li {
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  margin-bottom: 12px;
  line-height: 1.6;
}

.footer-bottom {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding: 24px 0;
}

.footer-bottom-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.copyright {
  color: rgba(255, 255, 255, 0.5);
  font-size: 14px;
  margin: 0;
}

.footer-copyright-block {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.icp-link {
  color: rgba(255, 255, 255, 0.5);
  font-size: 13px;
  text-decoration: none;
  transition: color 0.3s ease;
}

.icp-link:hover {
  color: #2196F3;
}

.footer-bottom-links {
  display: flex;
  align-items: center;
  gap: 16px;
}

.footer-bottom-links a {
  color: rgba(255, 255, 255, 0.5);
  text-decoration: none;
  font-size: 13px;
  transition: color 0.3s ease;
}

.footer-bottom-links a:hover {
  color: #2196F3;
}

.divider {
  color: rgba(255, 255, 255, 0.2);
}

/* ===== 隐私政策 / 使用条款弹窗 ===== */
.policy-mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  z-index: 999;
}

.policy-panel {
  background: #FFFFFF;
  color: #1A1A1A;
  border-radius: 12px;
  width: 100%;
  max-width: 680px;
  max-height: 78vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.3);
}

.policy-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
  border-bottom: 1px solid #E8ECF1;
}

.policy-head h4 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #0B4F8C;
}

.policy-close {
  border: none;
  background: transparent;
  font-size: 26px;
  line-height: 1;
  color: #98A2B3;
  cursor: pointer;
  padding: 0 4px;
}

.policy-close:hover {
  color: #1A1A1A;
}

.policy-body {
  padding: 20px 24px;
  overflow-y: auto;
  flex: 1;
}

.policy-para {
  margin: 0 0 14px;
  font-size: 14px;
  line-height: 1.9;
  color: #3A4657;
  text-align: justify;
}

.policy-para:last-child {
  margin-bottom: 0;
}

.policy-foot {
  padding: 14px 24px;
  border-top: 1px solid #E8ECF1;
  text-align: right;
}

.policy-ok {
  background: #0B4F8C;
  color: #FFFFFF;
  border: none;
  border-radius: 20px;
  padding: 8px 32px;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.3s ease;
}

.policy-ok:hover {
  background: #2196F3;
}

.policy-fade-enter-active,
.policy-fade-leave-active {
  transition: opacity 0.25s ease;
}

.policy-fade-enter-from,
.policy-fade-leave-to {
  opacity: 0;
}

/* ===== 移动端 ===== */
@media (max-width: 900px) {
  .footer-grid {
    grid-template-columns: 1fr 1fr;
    gap: 32px;
  }
  .footer-brand {
    grid-column: 1 / -1;
  }
}

@media (max-width: 480px) {
  .footer-grid {
    grid-template-columns: 1fr;
  }
  .footer-bottom-content {
    flex-direction: column;
    gap: 12px;
    text-align: center;
  }
  .footer-container {
    padding: 0 16px;
  }
}
</style>